import React, { FC, useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { DatePickerPopover } from '@modernloop/shared/components';
import { IsoTimestamp, assertIsoTimestamp, formatTZ, getLocalTimezone } from '@modernloop/shared/datetime';
import { DateIcon } from '@modernloop/shared/icons';
import { FormHelperText, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';

import { TrainingStatus } from 'src/generated/mloop-graphql';

type Props = {
  selectedDate: IsoTimestamp;
  setSelectedDate: (date: IsoTimestamp) => void;
  selectedRole: string;
  setSelectedRole: (role: TrainingStatus) => void;
  roleOptions: { key: string; label: string }[];
  isDateDisabled?: boolean;
};
const DEFAULT_DATE = '1970-01-01T00:00:01.000Z';

const ModalBody: FC<Props> = ({
  selectedDate,
  selectedRole,
  setSelectedDate,
  setSelectedRole,
  roleOptions,
  isDateDisabled,
}) => {
  const { t } = useTranslation();
  const timezone = getLocalTimezone();

  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateAnchorEl = useRef<HTMLInputElement>(null);

  return (
    <Stack spacing={2.5}>
      <Stack direction="column" spacing={1}>
        <Typography variant="subtitle1">
          {t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.modalBody.whenDidInterviewHappen')}
        </Typography>
        <TextField
          inputRef={dateAnchorEl}
          disabled={isDateDisabled}
          variant="outlined"
          value={selectedDate && selectedDate !== DEFAULT_DATE ? formatTZ(selectedDate, timezone, 'MMM d, yyyy') : ''}
          sx={{
            pointerEvents: isDateDisabled ? 'none' : undefined,
          }}
          inputProps={{ onClick: () => setShowDatePicker(true), readOnly: true }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          InputProps={{
            startAdornment: <DateIcon />,
          }}
        />
        {showDatePicker && (
          <DatePickerPopover
            open
            disableFuture
            disablePast={false}
            timezone={timezone}
            anchorEl={dateAnchorEl.current}
            date={
              selectedDate && selectedDate !== DEFAULT_DATE
                ? selectedDate
                : assertIsoTimestamp(new Date().toISOString())
            }
            onChange={(date: IsoTimestamp) => {
              setSelectedDate(date);
              setShowDatePicker(false);
            }}
            onClose={() => setShowDatePicker(false)}
          />
        )}
        {isDateDisabled && (
          <FormHelperText>
            {t('hiring-dashboard-interviewers:interviewers.moduleMemberSidepanel.modalBody.updateTaskToChangeDate')}
          </FormHelperText>
        )}{' '}
      </Stack>

      <Stack direction="column" spacing={1}>
        <Typography variant="subtitle1">
          {t('hiring-dashboard-interviewers:interviewers.moduleLog.column.role')}
        </Typography>
        <Select
          fullWidth
          onChange={(event) => setSelectedRole(event.target.value as TrainingStatus)}
          value={selectedRole}
        >
          {roleOptions.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </Stack>
  );
};

export default ModalBody;
